import { AppLayout } from '@/components/layout/app.layout';
import { ContentLoader } from '@/components/ui/loaders';
import { ScrollToTop } from '@/hooks/use-scroll-to-top';
import { useIsAuthenticated } from '@/services/auth';
import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/index.page'));
const SubscribePage = lazy(() => import('../pages/subscribe.page'));
const SubscribedPage = lazy(() => import('../pages/subscribed.page'));

const NotFoundPage = lazy(() => import('../pages/404.page'));
const SignInPage = lazy(() => import('../pages/sign-in.page'));

const DashboardPage = lazy(() => import('../pages/dashboard.page'));
const ProfilePage = lazy(() => import('../pages/profile.page'));

const UsersPage = lazy(() => import('../pages/users/page'));
const AddUserPage = lazy(() => import('../pages/users/add.page'));
const EditUserPage = lazy(() => import('../pages/users/edit.page'));

const EventsPage = lazy(() => import('../pages/events/page'));
const AddEventPage = lazy(() => import('../pages/events/add.page'));
const EditEventPage = lazy(() => import('../pages/events/edit.page'));

const CouplesPage = lazy(() => import('../pages/couples/page'));
const AddCouplePage = lazy(() => import('../pages/couples/add.page'));
const EditCouplePage = lazy(() => import('../pages/couples/edit.page'));

type ProtectedRouteProps = {
  isAuthenticated: boolean;
  children: React.ReactNode;
};

function ProtectedRoute({ isAuthenticated, children }: ProtectedRouteProps) {
  if (!isAuthenticated) {
    return <Navigate to="/entrar" replace />;
  }

  return children;
}

export function Router() {
  const { isAuthenticated, isLoading } = useIsAuthenticated();

  if (isLoading) {
    return <ContentLoader show />;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<ContentLoader show />}>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="inscricao" element={<SubscribePage />} />
          <Route path="inscricao-confirmada" element={<SubscribedPage />} />
          <Route path="subscribe" element={<Navigate to="/inscricao" replace />} />
          <Route path="subscribed" element={<Navigate to="/inscricao-confirmada" replace />} />
          <Route path="sign-in" element={<Navigate to="/entrar" replace />} />
          <Route
            path="/entrar"
            element={isAuthenticated ? <Navigate to="/admin" replace /> : <SignInPage />}
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="profile" element={<ProfilePage />} />

            <Route path="users">
              <Route index element={<UsersPage />} />
              <Route path="add" element={<AddUserPage />} />
              <Route path=":id/edit" element={<EditUserPage />} />
            </Route>

            <Route path="events">
              <Route index element={<EventsPage />} />
              <Route path="add" element={<AddEventPage />} />
              <Route path=":id/edit" element={<EditEventPage />} />
            </Route>

            <Route path="couples">
              <Route index element={<CouplesPage />} />
              <Route path="add" element={<AddCouplePage />} />
              <Route path=":id/edit" element={<EditCouplePage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default Router;
